import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useAuth } from '../../components/AuthProvider';
import { useNotification } from '../../components/member/NotificationProvider';
import { getReceivedOrders, updateOrderStatus, cleanupExpiredOrders } from '../../services/supabaseService';
import { supabase } from '../../lib/supabase';

interface OrderItem {
  id: number;
  product_id: number;
  quantity: number;
  unit_price: number;
  product?: {
    id: number;
    name: string;
    image_url?: string | null;
  };
}

interface Order {
  id: string | number;
  external_reference?: string;
  created_at: string;
  status: string;
  amount: number;
  shipping_price?: number;
  payment_method?: {
    id: number;
    name: string;
    slug?: string;
  };
  buyer_id?: number | string;
  buyer?: {
    id?: number | string;
    username?: string;
    fullname?: string;
    avatar?: string;
    email?: string;
  };
  order_items?: OrderItem[];
}

export const ReceivedOrders: React.FC = () => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [updatingId, setUpdatingId] = useState<string | number | null>(null);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      await cleanupExpiredOrders();
      const data = await getReceivedOrders();
      
      // Hydrate missing buyer usernames if needed
      const orderList: Order[] = data || [];
      const missingBuyerIds = Array.from(new Set(
        orderList
          .filter(o => o.buyer_id && (!o.buyer || !o.buyer.username))
          .map(o => Number(o.buyer_id))
          .filter(id => !isNaN(id))
      ));

      if (missingBuyerIds.length > 0) {
        try {
          const { data: buyers } = await (supabase.rpc('get_safe_users') as any)
            .select('id, username, fullname, avatar')
            .in('id', missingBuyerIds);

          if (buyers && buyers.length > 0) {
            const bMap = new Map(buyers.map((b: any) => [String(b.id), b]));
            orderList.forEach(o => {
              if (o.buyer_id && (!o.buyer || !o.buyer.username)) {
                const found = bMap.get(String(o.buyer_id));
                if (found) {
                  o.buyer = { ...(o.buyer || {}), ...(found as any) };
                }
              }
            });
          }
        } catch (rpcErr) {
          console.warn('Silent fallback for safe_users in ReceivedOrders:', rpcErr);
        }
      }

      setOrders(orderList);
    } catch (err) {
      console.error('Error fetching received orders:', err);
      showNotification('Erro ao carregar os pedidos recebidos.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return { label: 'Pago', class: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' };
      case 'pending':
      case 'pending_payment':
      case 'waiting_payment':
        return { label: 'Aguardando Pagamento', class: 'bg-amber-500/15 text-amber-400 border-amber-500/30' };
      case 'processing':
        return { label: 'Em Preparação', class: 'bg-blue-500/15 text-blue-400 border-blue-500/30' };
      case 'shipped':
        return { label: 'Enviado', class: 'bg-indigo-500/15 text-indigo-400 border-indigo-500/30' };
      case 'delivered':
        return { label: 'Entregue', class: 'bg-teal-500/15 text-teal-400 border-teal-500/30' };
      case 'expired':
        return { label: 'Expirado', class: 'bg-slate-700/40 text-slate-400 border-slate-600/30' };
      case 'canceled':
      case 'cancelled':
        return { label: 'Cancelado', class: 'bg-rose-500/15 text-rose-400 border-rose-500/30' };
      default:
        return { label: status, class: 'bg-slate-800 text-slate-300 border-slate-700' };
    }
  };

  const getAvailableStatusOptions = (currentStatus: string) => {
    const status = currentStatus || 'pending_payment';
    if (status === 'pending_payment' || status === 'waiting_payment' || status === 'pending') {
      return [
        { value: status, label: 'Pendente (Aguard. Pagamento)' },
        { value: 'paid', label: 'Marcar como Pago' },
        { value: 'cancelled', label: 'Cancelar Pedido' }
      ];
    }
    if (status === 'paid') {
      return [
        { value: 'paid', label: 'Pago' },
        { value: 'processing', label: 'Iniciar Preparação' },
        { value: 'shipped', label: 'Marcar como Enviado' },
        { value: 'delivered', label: 'Marcar como Entregue' }
      ];
    }
    if (status === 'processing') {
      return [
        { value: 'processing', label: 'Em Preparação' },
        { value: 'shipped', label: 'Marcar como Enviado' },
        { value: 'delivered', label: 'Marcar como Entregue' }
      ];
    }
    if (status === 'shipped') {
      return [
        { value: 'shipped', label: 'Enviado' },
        { value: 'delivered', label: 'Marcar como Entregue' }
      ];
    }
    return [];
  };

  const handleStatusChange = async (orderId: string | number, newStatus: string) => {
    const orderObj = orders.find(o => String(o.id) === String(orderId));
    if (!orderObj || orderObj.status === newStatus) return;

    const isFinal = ['cancelled', 'canceled', 'expired', 'refunded', 'delivered'].includes(orderObj.status);
    if (isFinal) {
      Swal.fire({
        title: 'Status Final',
        text: 'Este pedido já está finalizado e seu status não pode ser alterado.',
        icon: 'warning',
        confirmButtonColor: '#9333ea',
        background: '#0f172a',
        color: '#fff'
      });
      return;
    }

    const confirm = await Swal.fire({
      title: 'Alterar Status?',
      text: `Deseja atualizar este pedido para "${newStatus.toUpperCase()}"?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sim, atualizar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#9333ea',
      cancelButtonColor: '#334155',
      background: '#0f172a',
      color: '#fff'
    });

    if (!confirm.isConfirmed) return;

    setUpdatingId(orderId);
    try {
      const res = await updateOrderStatus(String(orderId), newStatus);
      if (res?.ok) {
        setOrders(prev => prev.map(o => String(o.id) === String(orderId) ? { ...o, status: newStatus } : o));
        showNotification('Status do pedido atualizado com sucesso!', 'success');
      } else {
        throw new Error(res?.error || 'Erro ao atualizar');
      }
    } catch (err: any) {
      console.error('Status change error:', err);
      showNotification(`Falha ao alterar status: ${err.message || 'Erro de conexão'}`, 'error');
    } finally {
      setUpdatingId(null);
    }
  };

  // Extract unique available months from orders
  const availableMonths = useMemo(() => {
    const monthsMap = new Map<string, string>();
    orders.forEach(o => {
      if (!o.created_at) return;
      const d = new Date(o.created_at);
      if (isNaN(d.getTime())) return;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!monthsMap.has(key)) {
        const rawLabel = d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
        const label = rawLabel.charAt(0).toUpperCase() + rawLabel.slice(1);
        monthsMap.set(key, label);
      }
    });

    // Ensure current month is in the list
    const now = new Date();
    const currentKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    if (!monthsMap.has(currentKey)) {
      const rawCurrent = now.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
      monthsMap.set(currentKey, rawCurrent.charAt(0).toUpperCase() + rawCurrent.slice(1));
    }

    return Array.from(monthsMap.entries())
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([key, label]) => ({ key, label }));
  }, [orders]);

  // Metrics (Revenue calculated strictly for current month)
  const metrics = useMemo(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    const currentMonthLabel = now.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
    const formattedMonthBadge = currentMonthLabel.charAt(0).toUpperCase() + currentMonthLabel.slice(1);

    const totalCount = orders.length;
    const pendingCount = orders.filter(o => ['pending', 'pending_payment', 'waiting_payment'].includes(o.status)).length;
    const paidOrders = orders.filter(o => ['paid', 'processing', 'shipped', 'delivered'].includes(o.status));
    const paidCount = paidOrders.length;

    // Filter paid orders strictly belonging to current month
    const currentMonthPaidOrders = paidOrders.filter(o => {
      if (!o.created_at) return false;
      const d = new Date(o.created_at);
      if (isNaN(d.getTime())) return false;
      return d.getFullYear() === currentYear && d.getMonth() === currentMonth;
    });

    const currentMonthRevenue = currentMonthPaidOrders.reduce((sum, o) => sum + (Number(o.amount) || 0), 0);
    const currentMonthPaidCount = currentMonthPaidOrders.length;

    return {
      totalCount,
      pendingCount,
      paidCount,
      currentMonthRevenue,
      currentMonthPaidCount,
      formattedMonthBadge,
    };
  }, [orders]);

  // Filtering
  const filteredOrders = useMemo(() => {
    return orders.filter(order => {
      // Month filter
      if (selectedMonth !== 'all') {
        if (!order.created_at) return false;
        const d = new Date(order.created_at);
        if (isNaN(d.getTime())) return false;
        const orderMonthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        if (orderMonthKey !== selectedMonth) return false;
      }

      // Status filter
      if (statusFilter !== 'all') {
        if (statusFilter === 'pending') {
          if (!['pending', 'pending_payment', 'waiting_payment'].includes(order.status)) return false;
        } else if (statusFilter === 'cancelled') {
          if (!['cancelled', 'canceled', 'expired'].includes(order.status)) return false;
        } else if (order.status !== statusFilter) {
          return false;
        }
      }

      // Search filter
      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase();
        const ref = (order.external_reference || String(order.id)).toLowerCase();
        const buyerName = (order.buyer?.fullname || '').toLowerCase();
        const buyerUser = (order.buyer?.username || '').toLowerCase();
        const hasItem = (order.order_items || []).some(item => (item.product?.name || '').toLowerCase().includes(term));

        if (!ref.includes(term) && !buyerName.includes(term) && !buyerUser.includes(term) && !hasItem) {
          return false;
        }
      }

      return true;
    });
  }, [orders, selectedMonth, statusFilter, searchTerm]);

  if (!user) {
    return (
      <div className="p-12 text-center text-slate-500 font-bold text-sm">
        Faça login para gerenciar os pedidos recebidos.
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-300 pb-12">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-black text-white italic tracking-tighter uppercase">
              Pedidos Recebidos
            </h1>
          </div>
          <p className="text-xs text-slate-400 font-medium mt-0.5">
            Gerencie as vendas, acompanhe o faturamento e atualize o status dos pedidos
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={fetchOrders}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-2 bg-slate-900 hover:bg-slate-800 text-slate-300 hover:text-white rounded-xl border border-slate-800 text-xs font-bold transition-colors cursor-pointer"
            title="Atualizar lista"
          >
            <i className={`fas fa-rotate-right ${loading ? 'animate-spin' : ''}`}></i>
            <span className="hidden sm:inline">Atualizar</span>
          </button>
          <Link
            to="/pedido-balcao"
            className="flex items-center gap-1.5 px-3.5 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-xl text-xs font-bold transition-all shadow-md shadow-purple-900/30 cursor-pointer"
          >
            <i className="fas fa-cash-register"></i>
            <span>Pedido no Balcão</span>
          </Link>
        </div>
      </div>

      {/* Metrics Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-slate-900/70 border border-slate-800 rounded-xl p-4">
          <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">Total de Pedidos</p>
          <p className="text-xl font-black text-white mt-1">{metrics.totalCount}</p>
        </div>
        <div className="bg-slate-900/70 border border-slate-800 rounded-xl p-4">
          <p className="text-[10px] font-black uppercase tracking-wider text-amber-500/90 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse"></span>
            Aguardando Pagamento
          </p>
          <p className="text-xl font-black text-amber-400 mt-1">{metrics.pendingCount}</p>
        </div>
        <div className="bg-slate-900/70 border border-slate-800 rounded-xl p-4">
          <p className="text-[10px] font-black uppercase tracking-wider text-emerald-500/90 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
            Pedidos Pagos
          </p>
          <p className="text-xl font-black text-emerald-400 mt-1">{metrics.paidCount}</p>
        </div>
        <div className="bg-slate-900/70 border border-purple-500/20 bg-purple-950/10 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-black uppercase tracking-wider text-purple-400">Faturamento (Mês Atual)</p>
            <span className="text-[9px] font-bold text-purple-300 bg-purple-500/20 border border-purple-500/30 px-1.5 py-0.5 rounded uppercase">
              {metrics.formattedMonthBadge}
            </span>
          </div>
          <p className="text-xl font-black text-white mt-1">
            R$ {metrics.currentMonthRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="bg-slate-900/50 border border-slate-800/80 rounded-xl p-3 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
        {/* Search */}
        <div className="relative flex-1">
          <i className="fas fa-search absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-xs"></i>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por #ID, cliente ou produto..."
            className="w-full bg-slate-950/60 border border-slate-800 text-slate-200 placeholder:text-slate-500 pl-9 pr-8 py-2 rounded-lg text-xs font-medium focus:outline-none focus:border-purple-500/60 transition-colors"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white text-xs"
            >
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>

        {/* Filters Group (Month & Status) */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Month Dropdown */}
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider shrink-0 hidden sm:inline">
              Mês:
            </span>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="bg-slate-950/60 border border-slate-800 text-slate-200 px-3 py-2 rounded-lg text-xs font-bold focus:outline-none focus:border-purple-500/60 cursor-pointer"
            >
              <option value="all">Todos os Meses</option>
              {availableMonths.map((m) => (
                <option key={m.key} value={m.key}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>

          {/* Status Dropdown */}
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider shrink-0 hidden sm:inline">
              Status:
            </span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-slate-950/60 border border-slate-800 text-slate-200 px-3 py-2 rounded-lg text-xs font-bold focus:outline-none focus:border-purple-500/60 cursor-pointer"
            >
              <option value="all">Todos os Status</option>
              <option value="pending">Aguardando Pagamento</option>
              <option value="paid">Pagos</option>
              <option value="processing">Em Preparação</option>
              <option value="shipped">Enviados</option>
              <option value="delivered">Entregues</option>
              <option value="cancelled">Cancelados / Expirados</option>
            </select>
          </div>
        </div>
      </div>

      {/* Orders List */}
      {loading ? (
        <div className="py-20 text-center space-y-3">
          <div className="w-8 h-8 border-3 border-purple-500/20 border-t-purple-500 rounded-full animate-spin mx-auto"></div>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Carregando pedidos recebidos...</p>
        </div>
      ) : filteredOrders.length > 0 ? (
        <div className="space-y-3">
          {filteredOrders.map((order) => {
            const badge = getStatusBadge(order.status);
            const availableOptions = getAvailableStatusOptions(order.status);
            const totalQty = order.order_items?.reduce((acc, item) => acc + (item.quantity || 1), 0) || 0;
            const refCode = order.external_reference
              ? order.external_reference.slice(-7).toUpperCase()
              : String(order.id).slice(-7).toUpperCase();

            const buyerInitials = order.buyer?.fullname
              ? order.buyer.fullname.slice(0, 2).toUpperCase()
              : order.buyer?.username
              ? order.buyer.username.slice(0, 2).toUpperCase()
              : 'CL';

            return (
              <div
                key={order.id}
                className="bg-slate-900/60 border border-slate-800 hover:border-slate-700 rounded-xl p-4 sm:p-5 transition-all group"
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  {/* Left: Info & Buyer */}
                  <div className="flex items-start sm:items-center gap-3.5 flex-1 min-w-0">
                    {/* Buyer Avatar */}
                    <div className="relative shrink-0">
                      {order.buyer?.avatar ? (
                        <img
                          src={order.buyer.avatar}
                          alt={order.buyer?.username || 'Cliente'}
                          className="w-10 h-10 rounded-full object-cover border border-slate-700"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-xs font-black text-purple-400">
                          {buyerInitials}
                        </div>
                      )}
                    </div>

                    {/* Order Details */}
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <Link
                          to={`/pedidos-recebidos/${order.id}`}
                          className="text-sm font-black text-white hover:text-purple-400 transition-colors uppercase tracking-tight flex items-center gap-1.5"
                        >
                          <span>#{refCode}</span>
                          <i className="fas fa-arrow-up-right-from-square text-[10px] text-slate-500 opacity-60 group-hover:opacity-100"></i>
                        </Link>
                        <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-full border ${badge.class}`}>
                          {badge.label}
                        </span>
                      </div>

                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-400">
                        <span className="font-bold text-slate-300">
                          {order.buyer?.fullname || order.buyer?.username ? (
                            order.buyer.username ? (
                              <Link
                                to={`/profile/${order.buyer.username}`}
                                className="hover:text-purple-400 transition-colors"
                              >
                                {order.buyer.fullname || order.buyer.username}
                                <span className="text-slate-500 font-normal ml-1">@{order.buyer.username}</span>
                              </Link>
                            ) : (
                              order.buyer.fullname
                            )
                          ) : (
                            'Cliente Convidado'
                          )}
                        </span>
                        <span className="text-slate-600">•</span>
                        <span className="text-[11px] text-slate-500">
                          {new Date(order.created_at).toLocaleDateString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                        {order.payment_method?.name && (
                          <>
                            <span className="text-slate-600">•</span>
                            <span className="text-[11px] text-purple-400 font-semibold flex items-center gap-1">
                              <i className="fas fa-credit-card text-[10px]"></i>
                              {order.payment_method.name}
                            </span>
                          </>
                        )}
                      </div>

                      {/* Items Preview */}
                      <div className="mt-2 flex items-center gap-2 text-xs text-slate-400">
                        <span className="font-bold text-slate-300">
                          {totalQty} {totalQty === 1 ? 'item' : 'itens'}
                        </span>
                        {order.order_items && order.order_items.length > 0 && (
                          <span className="text-slate-500 truncate max-w-sm hidden sm:inline">
                            : {order.order_items.slice(0, 2).map(item => item.product?.name).filter(Boolean).join(', ')}
                            {order.order_items.length > 2 ? ` +${order.order_items.length - 2}` : ''}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right: Price & Quick Action */}
                  <div className="flex items-center justify-between sm:justify-end gap-4 shrink-0 pt-3 lg:pt-0 border-t lg:border-t-0 border-slate-800/80">
                    <div className="text-left sm:text-right">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block">Total</span>
                      <span className="text-base font-black text-white">
                        R$ {(Number(order.amount) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      {/* Status Selector */}
                      {availableOptions.length > 0 && (
                        <div className="relative">
                          <select
                            disabled={updatingId === order.id}
                            value={order.status || ''}
                            onChange={(e) => handleStatusChange(order.id, e.target.value)}
                            className="bg-slate-950 border border-slate-700/80 text-slate-200 text-xs font-bold rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-purple-500 appearance-none pr-7 cursor-pointer hover:border-slate-600 transition-colors disabled:opacity-50"
                          >
                            {availableOptions.map(opt => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                          <i className="fas fa-chevron-down absolute right-2 top-1/2 -translate-y-1/2 text-[9px] text-slate-500 pointer-events-none"></i>
                        </div>
                      )}

                      {/* Details Link */}
                      <Link
                        to={`/pedidos-recebidos/${order.id}`}
                        className="px-3 py-1.5 bg-slate-800 hover:bg-purple-600 text-slate-300 hover:text-white rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5"
                      >
                        <span>Detalhes</span>
                        <i className="fas fa-chevron-right text-[10px]"></i>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-slate-900/40 border border-slate-800/80 rounded-xl p-12 text-center space-y-3">
          <div className="w-12 h-12 rounded-full bg-slate-800/60 border border-slate-700 flex items-center justify-center text-slate-500 mx-auto">
            <i className="fas fa-clipboard-check text-xl"></i>
          </div>
          <h3 className="text-sm font-bold text-white uppercase tracking-wider">Nenhum pedido encontrado</h3>
          <p className="text-xs text-slate-500 max-w-sm mx-auto">
            {searchTerm || statusFilter !== 'all' || selectedMonth !== 'all'
              ? 'Nenhum pedido corresponde aos filtros ou busca selecionados.'
              : 'Sua loja ainda não recebeu pedidos através do cardume.'}
          </p>
          {(searchTerm || statusFilter !== 'all' || selectedMonth !== 'all') && (
            <button
              onClick={() => { setSearchTerm(''); setStatusFilter('all'); setSelectedMonth('all'); }}
              className="text-xs text-purple-400 hover:text-purple-300 font-bold underline cursor-pointer"
            >
              Limpar filtros
            </button>
          )}
        </div>
      )}
    </div>
  );
};
